import type { FinancialsFilterState } from './financialsMockData'

export interface BaseRow {
  calendar: string
  season: string
  department: string
  className: string
  subclass: string
  salesDollar: number
  costDollar: number
  salesAUC: number
  salesAUR: number
  salesUnits: number
}

export interface Metrics {
  salesDollar: number
  costDollar: number
  salesAUC: number
  salesAUR: number
  salesUnits: number
  marginDollar: number
  gmPct: number
  discPct: number
  retailDollar: number
  landedCostDollar: number
  fobCostDollar: number
  units: number
  rcptImuPct: number
  rcptAur: number
}

export interface MetricGroups {
  planned: Metrics
  assortment: Metrics
  varDollar: Metrics
  varPercent: Metrics
}

export type GroupKey = keyof MetricGroups

export const METRIC_FIELDS: Array<keyof Metrics> = [
  'salesDollar',
  'costDollar',
  'salesAUC',
  'salesAUR',
  'salesUnits',
  'marginDollar',
  'gmPct',
  'discPct',
  'retailDollar',
  'landedCostDollar',
  'fobCostDollar',
  'units',
  'rcptImuPct',
  'rcptAur',
]

export const METRIC_LABELS: Record<keyof Metrics, string> = {
  salesDollar: 'Sales $',
  costDollar: 'Cost $',
  salesAUC: 'Sales AUC',
  salesAUR: 'Sales AUR',
  salesUnits: 'Sales Units',
  marginDollar: 'Margin $',
  gmPct: 'GM%',
  discPct: 'Disc%',
  retailDollar: 'Retail $',
  landedCostDollar: 'Landed Cost $',
  fobCostDollar: 'FOB Cost $',
  units: 'Units',
  rcptImuPct: 'Rcpt IMU %',
  rcptAur: 'Rcpt AUR',
}

export const PERCENT_FIELDS = new Set<keyof Metrics>([
  'gmPct',
  'discPct',
  'rcptImuPct',
])
export const CURRENCY_FIELDS = new Set<keyof Metrics>([
  'salesDollar',
  'costDollar',
  'marginDollar',
  'retailDollar',
  'landedCostDollar',
  'fobCostDollar',
])
export const UNIT_FIELDS = new Set<keyof Metrics>(['salesUnits', 'units'])
export const PRICE_FIELDS = new Set<keyof Metrics>([
  'salesAUC',
  'salesAUR',
  'rcptAur',
])

const BASE_ROWS: BaseRow[] = [
  {
    calendar: 'Seasonal Plan Calendar 2027',
    season: 'Spring',
    department: 'DEPT-023 - Tops',
    className: 'CLS-066 - Tees & Tanks',
    subclass: 'SUB-158 - Basic Tees',
    salesDollar: 4089247,
    costDollar: 1473310,
    salesAUC: 5.32,
    salesAUR: 13.0,
    salesUnits: 314617,
  },
  {
    calendar: 'Seasonal Plan Calendar 2027',
    season: 'Spring',
    department: 'DEPT-023 - Tops',
    className: 'CLS-066 - Tees & Tanks',
    subclass: 'SUB-159 - Crop Tops',
    salesDollar: 873859,
    costDollar: 356286,
    salesAUC: 1.46,
    salesAUR: 3.59,
    salesUnits: 243630,
  },
  {
    calendar: 'Seasonal Plan Calendar 2027',
    season: 'Spring',
    department: 'DEPT-023 - Tops',
    className: 'CLS-066 - Tees & Tanks',
    subclass: 'SUB-160 - Graphic Tees',
    salesDollar: 2412633,
    costDollar: 977028,
    salesAUC: 7.87,
    salesAUR: 19.43,
    salesUnits: 123428,
  },
  {
    calendar: 'Seasonal Plan Calendar 2027',
    season: 'Spring',
    department: 'DEPT-023 - Tops',
    className: 'CLS-066 - Tees & Tanks',
    subclass: 'SUB-161 - Tank Tops & Camis',
    salesDollar: 997796,
    costDollar: 395178,
    salesAUC: 3.31,
    salesAUR: 8.08,
    salesUnits: 116418,
  },
  {
    calendar: 'Seasonal Plan Calendar 2027',
    season: 'Spring',
    department: 'DEPT-023 - Tops',
    className: 'CLS-064 - Shirts & Blouses',
    subclass: 'SUB-152 - Blouses',
    salesDollar: 311298,
    costDollar: 133835,
    salesAUC: 1.0,
    salesAUR: 2.59,
    salesUnits: 115659,
  },
  {
    calendar: 'Seasonal Plan Calendar 2027',
    season: 'Spring',
    department: 'DEPT-023 - Tops',
    className: 'CLS-064 - Shirts & Blouses',
    subclass: 'SUB-153 - Button-Down Shirts',
    salesDollar: 929841,
    costDollar: 363826,
    salesAUC: 2.94,
    salesAUR: 7.18,
    salesUnits: 129566,
  },
  {
    calendar: 'Seasonal Plan Calendar 2027',
    season: 'Spring',
    department: 'DEPT-023 - Tops',
    className: 'CLS-064 - Shirts & Blouses',
    subclass: 'SUB-154 - Tunic Tops',
    salesDollar: 1121993,
    costDollar: 391058,
    salesAUC: 4.86,
    salesAUR: 12.04,
    salesUnits: 93188,
  },
  {
    calendar: 'Seasonal Plan Calendar 2027',
    season: 'Spring',
    department: 'DEPT-023 - Tops',
    className: 'CLS-065 - Sweaters & Knits',
    subclass: 'SUB-155 - Cardigans',
    salesDollar: 509134,
    costDollar: 209117,
    salesAUC: 9.42,
    salesAUR: 23.27,
    salesUnits: 21876,
  },
  {
    calendar: 'Seasonal Plan Calendar 2027',
    season: 'Spring',
    department: 'DEPT-023 - Tops',
    className: 'CLS-065 - Sweaters & Knits',
    subclass: 'SUB-156 - Pullovers',
    salesDollar: 1153680,
    costDollar: 382462,
    salesAUC: 2.04,
    salesAUR: 5.13,
    salesUnits: 224957,
  },
  {
    calendar: 'Seasonal Plan Calendar 2027',
    season: 'Spring',
    department: 'DEPT-023 - Tops',
    className: 'CLS-065 - Sweaters & Knits',
    subclass: 'SUB-157 - Turtlenecks',
    salesDollar: 159490,
    costDollar: 47546,
    salesAUC: 1.21,
    salesAUR: 3.0,
    salesUnits: 53217,
  },
  {
    calendar: 'Seasonal Plan Calendar 2027',
    season: 'Spring',
    department: 'DEPT-010 - Girls',
    className: 'CLS-029 - Tops',
    subclass: 'SUB-068 - Shirts & Blouses',
    salesDollar: 2146073,
    costDollar: 847880,
    salesAUC: 3.16,
    salesAUR: 5.66,
    salesUnits: 558887,
  },
  {
    calendar: 'Seasonal Plan Calendar 2027',
    season: 'Spring',
    department: 'DEPT-010 - Girls',
    className: 'CLS-029 - Tops',
    subclass: 'SUB-069 - Sweaters & Knits',
    salesDollar: 2112000,
    costDollar: 595490,
    salesAUC: 0.85,
    salesAUR: 3.01,
    salesUnits: 689145,
  },
  {
    calendar: 'Seasonal Plan Calendar 2027',
    season: 'Spring',
    department: 'DEPT-010 - Girls',
    className: 'CLS-029 - Tops',
    subclass: 'SUB-070 - Tees & Tanks',
    salesDollar: 2230000,
    costDollar: 945985,
    salesAUC: 2.96,
    salesAUR: 7.94,
    salesUnits: 280805,
  },
  {
    calendar: 'Seasonal Plan Calendar 2027',
    season: 'Spring',
    department: 'DEPT-010 - Girls',
    className: 'CLS-027 - Dresses & Skirts',
    subclass: 'SUB-064 - Casual Dresses',
    salesDollar: 815542,
    costDollar: 310366,
    salesAUC: 2.69,
    salesAUR: 6.42,
    salesUnits: 126994,
  },
  {
    calendar: 'Seasonal Plan Calendar 2027',
    season: 'Spring',
    department: 'DEPT-010 - Girls',
    className: 'CLS-027 - Dresses & Skirts',
    subclass: 'SUB-065 - Occasion Dresses',
    salesDollar: 268500,
    costDollar: 86236,
    salesAUC: 0.88,
    salesAUR: 2.57,
    salesUnits: 104474,
  },
  {
    calendar: 'Seasonal Plan Calendar 2027',
    season: 'Fall',
    department: 'DEPT-009 - Boys',
    className: 'CLS-024 - Outerwear',
    subclass: 'SUB-056 - Coats & Puffers',
    salesDollar: 5612400,
    costDollar: 2018100,
    salesAUC: 12.35,
    salesAUR: 30.85,
    salesUnits: 181924,
  },
  {
    calendar: 'Seasonal Plan Calendar 2027',
    season: 'Fall',
    department: 'DEPT-009 - Boys',
    className: 'CLS-024 - Outerwear',
    subclass: 'SUB-057 - Jackets',
    salesDollar: 3893750,
    costDollar: 1450400,
    salesAUC: 18.2,
    salesAUR: 44.99,
    salesUnits: 86552,
  },
  {
    calendar: 'Seasonal Plan Calendar 2027',
    season: 'Fall',
    department: 'DEPT-009 - Boys',
    className: 'CLS-023 - Bottoms',
    subclass: 'SUB-053 - Jeans & Denim',
    salesDollar: 2750300,
    costDollar: 1015100,
    salesAUC: 3.86,
    salesAUR: 9.62,
    salesUnits: 285893,
  },
  {
    calendar: 'Seasonal Plan Calendar 2027',
    season: 'Fall',
    department: 'DEPT-009 - Boys',
    className: 'CLS-023 - Bottoms',
    subclass: 'SUB-054 - Pants & Joggers',
    salesDollar: 4218600,
    costDollar: 1538900,
    salesAUC: 7.21,
    salesAUR: 17.95,
    salesUnits: 235024,
  },
  {
    calendar: 'Seasonal Plan Calendar 2027',
    season: 'Fall',
    department: 'DEPT-009 - Boys',
    className: 'CLS-025 - Tops',
    subclass: 'SUB-059 - Sweaters & Hoodies',
    salesDollar: 1986400,
    costDollar: 754880,
    salesAUC: 22.18,
    salesAUR: 53.04,
    salesUnits: 37451,
  },
  {
    calendar: 'Seasonal Plan Calendar 2027',
    season: 'Fall',
    department: 'DEPT-009 - Boys',
    className: 'CLS-025 - Tops',
    subclass: 'SUB-060 - Tees & Tanks',
    salesDollar: 2412000,
    costDollar: 891290,
    salesAUC: 5.45,
    salesAUR: 13.57,
    salesUnits: 177742,
  },
  {
    calendar: 'Seasonal Plan Calendar 2027',
    season: 'Winter',
    department: 'DEPT-020 - Dresses',
    className: 'CLS-058 - Occasion',
    subclass: 'SUB-138 - Maxi Dresses',
    salesDollar: 4920500,
    costDollar: 1832110,
    salesAUC: 8.45,
    salesAUR: 21.39,
    salesUnits: 230053,
  },
  {
    calendar: 'Seasonal Plan Calendar 2027',
    season: 'Winter',
    department: 'DEPT-020 - Dresses',
    className: 'CLS-058 - Occasion',
    subclass: 'SUB-139 - Midi Dresses',
    salesDollar: 3128400,
    costDollar: 1216240,
    salesAUC: 10.62,
    salesAUR: 26.21,
    salesUnits: 119371,
  },
  {
    calendar: 'Seasonal Plan Calendar 2027',
    season: 'Winter',
    department: 'DEPT-020 - Dresses',
    className: 'CLS-057 - Casual',
    subclass: 'SUB-135 - Shirt Dresses',
    salesDollar: 1689200,
    costDollar: 658910,
    salesAUC: 6.94,
    salesAUR: 16.95,
    salesUnits: 99664,
  },
  {
    calendar: 'Seasonal Plan Calendar 2027',
    season: 'Winter',
    department: 'DEPT-020 - Dresses',
    className: 'CLS-057 - Casual',
    subclass: 'SUB-137 - Wrap Dresses',
    salesDollar: 2240800,
    costDollar: 866120,
    salesAUC: 7.81,
    salesAUR: 19.45,
    salesUnits: 115209,
  },
]

export interface ProcessedBreakdownRow {
  calendar: string
  season: string
  department: string
  className: string
  subclass: string
  metrics: MetricGroups
}

export interface ClassGroup {
  className: string
  classKey: string
  metrics: MetricGroups
  rows: ProcessedBreakdownRow[]
}

export interface DeptGroup {
  calendar: string
  season: string
  department: string
  deptKey: string
  metrics: MetricGroups
  classes: ClassGroup[]
}

function hashString(input: string): number {
  let h = 2166136261
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

function normalizedFromHash(seed: number, salt: string): number {
  return (hashString(`${seed}:${salt}`) % 10000) / 10000
}

function shrinkForFilter(filterValue: string, filterName: string): number {
  const seed = hashString(filterValue)
  const n = normalizedFromHash(seed, filterName)
  return 0.4 + n * 0.35
}

function activeFilterPairs(
  filters: FinancialsFilterState
): Array<[string, string]> {
  const pairs: Array<[string, string]> = []
  if (filters.planId) pairs.push(['plan', filters.planId])
  if (filters.departmentId) pairs.push(['dept', filters.departmentId])
  if (filters.classId) pairs.push(['class', filters.classId])
  if (filters.subclassId) pairs.push(['subclass', filters.subclassId])
  if (filters.supplyChainCalendarId)
    pairs.push(['supplyCal', filters.supplyChainCalendarId])
  if (filters.seasonCalendarId)
    pairs.push(['seasonCal', filters.seasonCalendarId])
  if (filters.planStatus) pairs.push(['status', filters.planStatus])
  return pairs
}

const ZERO_METRICS: Metrics = {
  salesDollar: 0,
  costDollar: 0,
  salesAUC: 0,
  salesAUR: 0,
  salesUnits: 0,
  marginDollar: 0,
  gmPct: 0,
  discPct: 0,
  retailDollar: 0,
  landedCostDollar: 0,
  fobCostDollar: 0,
  units: 0,
  rcptImuPct: 0,
  rcptAur: 0,
}

function derivePlannedMetrics(row: BaseRow, seed: number): Metrics {
  const marginDollar = row.salesDollar - row.costDollar
  const gmPct = row.salesDollar > 0 ? (marginDollar / row.salesDollar) * 100 : 0
  const discPct = 25 + normalizedFromHash(seed, 'disc') * 30 // 25-55%
  const retailDollar = row.salesDollar * (1 + discPct / 100)
  const landedCostFactor = 0.82 + normalizedFromHash(seed, 'landed') * 0.1
  const landedCostDollar = row.costDollar * landedCostFactor
  const fobCostDollar = landedCostDollar * 0.82
  const units = row.salesUnits * (1.02 + normalizedFromHash(seed, 'u') * 0.1)
  const rcptImuPct =
    retailDollar > 0
      ? ((retailDollar - landedCostDollar) / retailDollar) * 100
      : 0
  const rcptAur = units > 0 ? retailDollar / units : 0
  return {
    salesDollar: row.salesDollar,
    costDollar: row.costDollar,
    salesAUC: row.salesAUC,
    salesAUR: row.salesAUR,
    salesUnits: row.salesUnits,
    marginDollar,
    gmPct,
    discPct,
    retailDollar,
    landedCostDollar,
    fobCostDollar,
    units,
    rcptImuPct,
    rcptAur,
  }
}

function deriveAssortment(planned: Metrics, seed: number): Metrics {
  // Assortment differs from planned by hash-based per-metric factor 0.7-1.3.
  const f = (salt: string) => 0.7 + normalizedFromHash(seed, salt) * 0.6
  const out: Metrics = { ...planned }
  out.salesDollar = planned.salesDollar * f('aSales')
  out.costDollar = planned.costDollar * f('aCost')
  out.salesUnits = planned.salesUnits * f('aSU')
  out.salesAUC =
    out.salesUnits > 0 ? out.costDollar / out.salesUnits : planned.salesAUC
  out.salesAUR =
    out.salesUnits > 0 ? out.salesDollar / out.salesUnits : planned.salesAUR
  out.marginDollar = out.salesDollar - out.costDollar
  out.gmPct =
    out.salesDollar > 0 ? (out.marginDollar / out.salesDollar) * 100 : 0
  out.discPct =
    planned.discPct * (0.9 + normalizedFromHash(seed, 'aDisc') * 0.3)
  out.retailDollar = planned.retailDollar * f('aRetail')
  out.landedCostDollar = planned.landedCostDollar * f('aLanded')
  out.fobCostDollar = planned.fobCostDollar * f('aFob')
  out.units = planned.units * f('aUnits')
  out.rcptImuPct =
    out.retailDollar > 0
      ? ((out.retailDollar - out.landedCostDollar) / out.retailDollar) * 100
      : 0
  out.rcptAur = out.units > 0 ? out.retailDollar / out.units : 0
  return out
}

function computeVarDollar(planned: Metrics, assortment: Metrics): Metrics {
  const out: Partial<Metrics> = {}
  for (const f of METRIC_FIELDS) {
    out[f] = assortment[f] - planned[f]
  }
  return out as Metrics
}

function computeVarPercent(planned: Metrics, assortment: Metrics): Metrics {
  const out: Partial<Metrics> = {}
  for (const f of METRIC_FIELDS) {
    out[f] =
      planned[f] !== 0 ? ((assortment[f] - planned[f]) / planned[f]) * 100 : 0
  }
  return out as Metrics
}

function buildMetricGroups(
  planned: Metrics,
  assortment: Metrics
): MetricGroups {
  return {
    planned,
    assortment,
    varDollar: computeVarDollar(planned, assortment),
    varPercent: computeVarPercent(planned, assortment),
  }
}

function sumMetrics(list: Metrics[]): Metrics {
  if (list.length === 0) return { ...ZERO_METRICS }
  const sum: Partial<Metrics> = {}
  for (const f of METRIC_FIELDS) {
    sum[f] = list.reduce((acc, m) => acc + m[f], 0)
  }
  // For ratio / per-unit metrics, recompute from totals where it makes sense.
  const s = sum as Metrics
  s.gmPct = s.salesDollar > 0 ? (s.marginDollar / s.salesDollar) * 100 : 0
  s.discPct =
    list.length > 0 ? list.reduce((a, m) => a + m.discPct, 0) / list.length : 0
  s.salesAUC = s.salesUnits > 0 ? s.costDollar / s.salesUnits : 0
  s.salesAUR = s.salesUnits > 0 ? s.salesDollar / s.salesUnits : 0
  s.rcptImuPct =
    s.retailDollar > 0
      ? ((s.retailDollar - s.landedCostDollar) / s.retailDollar) * 100
      : 0
  s.rcptAur = s.units > 0 ? s.retailDollar / s.units : 0
  return s
}

function aggregateGroups(rows: ProcessedBreakdownRow[]): MetricGroups {
  const planned = sumMetrics(rows.map(r => r.metrics.planned))
  const assortment = sumMetrics(rows.map(r => r.metrics.assortment))
  return buildMetricGroups(planned, assortment)
}

export function buildBreakdownGroups(
  filters: FinancialsFilterState
): DeptGroup[] {
  const activePairs = activeFilterPairs(filters)
  const totalShrink = activePairs.reduce(
    (acc, [name, value]) => acc * shrinkForFilter(value, name),
    1
  )
  const wiggleSeed = activePairs.length
    ? hashString(activePairs.map(([n, v]) => `${n}=${v}`).join('|'))
    : 0

  const processed: ProcessedBreakdownRow[] = BASE_ROWS.map((row, index) => {
    let { salesDollar, costDollar, salesUnits, salesAUC, salesAUR } = row

    if (wiggleSeed !== 0) {
      const noise = (salt: string) =>
        1 + (normalizedFromHash(wiggleSeed, `${salt}${index}`) - 0.5) * 0.06
      salesDollar = salesDollar * totalShrink * noise('s')
      costDollar = costDollar * totalShrink * noise('c')
      salesUnits = salesUnits * totalShrink * noise('u')
      const ratioNoise = (salt: string) =>
        1 + (normalizedFromHash(wiggleSeed, `${salt}${index}`) - 0.5) * 0.1
      salesAUC = salesAUC * ratioNoise('auc')
      salesAUR = salesAUR * ratioNoise('aur')
    }

    const adjusted: BaseRow = {
      ...row,
      salesDollar,
      costDollar,
      salesUnits,
      salesAUC,
      salesAUR,
    }
    const rowSeed = hashString(
      `${row.department}|${row.className}|${row.subclass}|${wiggleSeed}`
    )
    const planned = derivePlannedMetrics(adjusted, rowSeed)
    const assortment = deriveAssortment(planned, rowSeed)

    return {
      calendar: row.calendar,
      season: row.season,
      department: row.department,
      className: row.className,
      subclass: row.subclass,
      metrics: buildMetricGroups(planned, assortment),
    }
  })

  const deptMap = new Map<string, DeptGroup>()
  for (const row of processed) {
    const deptKey = `${row.calendar}|${row.season}|${row.department}`
    let dept = deptMap.get(deptKey)
    if (!dept) {
      dept = {
        calendar: row.calendar,
        season: row.season,
        department: row.department,
        deptKey,
        metrics: buildMetricGroups({ ...ZERO_METRICS }, { ...ZERO_METRICS }),
        classes: [],
      }
      deptMap.set(deptKey, dept)
    }
    const classKey = `${deptKey}|${row.className}`
    let cls = dept.classes.find(c => c.classKey === classKey)
    if (!cls) {
      cls = {
        className: row.className,
        classKey,
        metrics: buildMetricGroups({ ...ZERO_METRICS }, { ...ZERO_METRICS }),
        rows: [],
      }
      dept.classes.push(cls)
    }
    cls.rows.push(row)
  }

  for (const dept of deptMap.values()) {
    for (const cls of dept.classes) {
      cls.metrics = aggregateGroups(cls.rows)
    }
    dept.metrics = aggregateGroups(dept.classes.flatMap(c => c.rows))
  }

  return Array.from(deptMap.values())
}
